/**
 * SendTrainingWizard - 4-step wizard for sending training
 * Extracted from TrainingView.jsx
 * Steps: 1. Driver → 2. Truck → 3. Video → 4. Company & Send
 */

import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Video, Send, User, Truck, ChevronRight, ArrowLeft,
  RefreshCw, Check, Building2, UserPlus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SendTrainingWizard({ 
  drivers, 
  trucks, 
  videos, 
  companies,
  onTrainingSent 
}) {
  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Search states
  const [driverSearch, setDriverSearch] = useState('');
  const [truckSearch, setTruckSearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  
  // Random Driver state
  const [showRandomDriver, setShowRandomDriver] = useState(false);
  const [randomDriverForm, setRandomDriverForm] = useState({
    full_name: '',
    email: '',
    truck_info: '',
    company_id: '',
    company_name: ''
  });

  const resetWizard = () => {
    setWizardStep(1);
    setSelectedDriver(null);
    setSelectedTruck(null);
    setSelectedVideo(null);
    setSelectedCompany(null);
    setDriverSearch('');
    setTruckSearch('');
    setCompanySearch('');
    setShowRandomDriver(false);
    setRandomDriverForm({ full_name: '', email: '', truck_info: '', company_id: '', company_name: '' });
  };

  const sendTraining = async () => {
    const isRandomDriver = showRandomDriver && randomDriverForm.full_name && randomDriverForm.email;
    
    if (!isRandomDriver && !selectedDriver) {
      toast.error('Please select a driver or fill in random driver details');
      return;
    }
    if (!selectedVideo) {
      toast.error('Please select a video');
      return;
    }
    
    const companyId = isRandomDriver ? randomDriverForm.company_id : selectedCompany?.company_id;
    const companyName = isRandomDriver 
      ? (randomDriverForm.company_name || companies.find(c => c.company_id === randomDriverForm.company_id)?.name || '')
      : (selectedCompany?.name || '');
    
    setSending(true);
    try {
      let driverData;
      let truckData = selectedTruck;
      
      if (isRandomDriver) {
        driverData = {
          driver_id: `random_${Date.now()}`,
          name: randomDriverForm.full_name,
          email: randomDriverForm.email,
          is_random: true,
          company_id: companyId,
          company_name: companyName
        };
        if (randomDriverForm.truck_info) {
          truckData = { truck_id: null, truck_number: randomDriverForm.truck_info };
        }
      } else {
        driverData = selectedDriver;
      }
      
      const linkRes = await axios.post(`${API}/training/generate-link`, {
        video_id: selectedVideo.video_id,
        driver_id: driverData.driver_id,
        driver_name: driverData.name,
        driver_email: driverData.email,
        truck_id: truckData?.truck_id || null,
        truck_number: truckData?.truck_number || null,
        company_id: companyId || null,
        company_name: companyName || null,
        is_random_driver: isRandomDriver
      }, { withCredentials: true });

      if (sendEmail) {
        try {
          await axios.post(`${API}/training/send-email`, {
            link_token: linkRes.data.link_token,
            driver_id: driverData.driver_id,
            driver_email: driverData.email,
            driver_name: driverData.name
          }, { withCredentials: true });
          toast.success(`Training sent to ${driverData.name}!`);
        } catch (emailError) {
          toast.error(emailError.response?.data?.detail || 'Email failed - but training link was generated.');
        }
      } else {
        toast.success('Training link generated!');
      }

      resetWizard();
      onTrainingSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send training');
    } finally {
      setSending(false);
    }
  };

  // Filter drivers based on search (with defensive array check)
  const safeDrivers = Array.isArray(drivers) ? drivers : [];
  const filteredDrivers = safeDrivers.filter(d => 
    d.name?.toLowerCase().includes(driverSearch.toLowerCase()) ||
    d.email?.toLowerCase().includes(driverSearch.toLowerCase())
  );

  // Filter trucks based on search (with defensive array check)
  const safeTrucks = Array.isArray(trucks) ? trucks : [];
  const filteredTrucks = safeTrucks.filter(t => 
    t.truck_number?.toLowerCase().includes(truckSearch.toLowerCase()) ||
    t.vin?.toLowerCase().includes(truckSearch.toLowerCase())
  );

  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-2xl border border-border/30 overflow-hidden">
      {/* Progress Steps */}
      <div className="p-4 border-b border-border/30">
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${wizardStep >= 1 ? 'bg-accent text-black' : 'bg-secondary text-muted-foreground'}`}>
            <User className="w-4 h-4" />
            <span className="font-medium text-sm">1. Driver</span>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${wizardStep >= 2 ? 'bg-accent text-black' : 'bg-secondary text-muted-foreground'}`}>
            <Truck className="w-4 h-4" />
            <span className="font-medium text-sm">2. Truck</span>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${wizardStep >= 3 ? 'bg-accent text-black' : 'bg-secondary text-muted-foreground'}`}>
            <Video className="w-4 h-4" />
            <span className="font-medium text-sm">3. Video</span>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
          <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${wizardStep >= 4 ? 'bg-purple-500 text-white' : 'bg-secondary text-muted-foreground'}`}>
            <Building2 className="w-4 h-4" />
            <span className="font-medium text-sm">4. Company</span>
          </div>
        </div>
      </div>

      {/* Selection Summary */}
      {(selectedDriver || selectedTruck || selectedVideo || selectedCompany) && (
        <div className="px-4 py-3 bg-accent/10 border-b border-border/30 flex flex-wrap items-center gap-4">
          {selectedDriver && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 rounded-lg">
              <User className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-blue-400">{selectedDriver.name}</span>
            </div>
          )}
          {selectedTruck && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-500/20 rounded-lg">
              <Truck className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-medium text-orange-400">{selectedTruck.truck_number}</span>
            </div>
          )}
          {selectedVideo && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 rounded-lg">
              <Video className="w-4 h-4 text-red-400" />
              <span className="text-sm font-medium text-red-400">{selectedVideo.title}</span>
            </div>
          )}
          {selectedCompany && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/20 rounded-lg">
              <Building2 className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-medium text-purple-400">{selectedCompany.name}</span>
            </div>
          )}
        </div>
      )}

      {/* Step 1: Select Driver */}
      {wizardStep === 1 && (
        <div className="p-4">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-accent" />
            Select Driver
          </h3>
          
          {/* Random Driver Option */}
          <div className="mb-4">
            <button
              onClick={() => setShowRandomDriver(!showRandomDriver)}
              className={`w-full p-4 rounded-xl border-2 border-dashed transition-colors flex items-center gap-3 ${showRandomDriver ? 'border-green-500 bg-green-500/10' : 'border-border hover:border-accent'}`}
              data-testid="random-driver-toggle"
            >
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-green-400" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-green-400">Random Driver (Not in list)</p>
                <p className="text-sm text-muted-foreground">Click to enter driver details manually</p>
              </div>
              <ChevronRight className={`w-5 h-5 text-muted-foreground transition-transform ${showRandomDriver ? 'rotate-90' : ''}`} />
            </button>
            
            {/* Random Driver Form */}
            {showRandomDriver && (
              <div className="mt-3 p-4 bg-secondary/50 rounded-xl space-y-3 border border-green-500/30">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-muted-foreground">Full Name *</label>
                    <input
                      type="text"
                      value={randomDriverForm.full_name}
                      onChange={e => setRandomDriverForm({...randomDriverForm, full_name: e.target.value})}
                      className="w-full mt-1 px-3 py-2 rounded-lg bg-secondary border border-border focus:border-accent outline-none"
                      placeholder="John Smith"
                      data-testid="random-driver-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Email *</label>
                    <input
                      type="email"
                      value={randomDriverForm.email}
                      onChange={e => setRandomDriverForm({...randomDriverForm, email: e.target.value})}
                      className="w-full mt-1 px-3 py-2 rounded-lg bg-secondary border border-border focus:border-accent outline-none"
                      placeholder="driver@company.com"
                      data-testid="random-driver-email"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-muted-foreground">Truck Info</label>
                    <input
                      type="text"
                      value={randomDriverForm.truck_info}
                      onChange={e => setRandomDriverForm({...randomDriverForm, truck_info: e.target.value})}
                      className="w-full mt-1 px-3 py-2 rounded-lg bg-secondary border border-border focus:border-accent outline-none"
                      placeholder="Truck #123 or VIN"
                      data-testid="random-driver-truck"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Company</label>
                    <select
                      value={randomDriverForm.company_id}
                      onChange={e => setRandomDriverForm({...randomDriverForm, company_id: e.target.value})}
                      className="w-full mt-1 px-3 py-2 rounded-lg bg-secondary border border-border focus:border-accent outline-none"
                      data-testid="random-driver-company"
                    >
                      <option value="">Select Company...</option>
                      {companies.map(c => (
                        <option key={c.company_id} value={c.company_id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {randomDriverForm.full_name && randomDriverForm.email && (
                  <button
                    onClick={() => setWizardStep(4)}
                    className="w-full py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-bold flex items-center justify-center gap-2"
                    data-testid="random-driver-continue"
                  >
                    Continue to Select Video
                    <ChevronRight className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}
          </div>
          
          <div className="text-center text-sm text-muted-foreground mb-4">— OR select from existing drivers —</div>
          
          <input
            type="text"
            placeholder="Search drivers by name or email..."
            value={driverSearch}
            onChange={e => setDriverSearch(e.target.value)}
            className="w-full mb-4 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
            data-testid="driver-search-input"
          />
          <div className="max-h-64 overflow-y-auto space-y-2">
            {filteredDrivers.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No drivers found. Add drivers in the Fleet section or use Random Driver above.
              </div>
            ) : (
              filteredDrivers.map(d => (
                <div
                  key={d.driver_id}
                  onClick={() => { setSelectedDriver(d); setShowRandomDriver(false); setWizardStep(2); }}
                  className="p-4 rounded-xl bg-secondary hover:bg-accent/20 cursor-pointer transition-colors flex items-center gap-3"
                  data-testid={`driver-option-${d.driver_id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{d.name}</p>
                    <p className="text-sm text-muted-foreground">{d.email || 'No email'}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Step 2: Select Truck */}
      {wizardStep === 2 && (
        <div className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => setWizardStep(1)} className="p-2 hover:bg-secondary rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Truck className="w-5 h-5 text-accent" />
              Select Truck for {selectedDriver?.name}
            </h3>
          </div>
          <input
            type="text"
            placeholder="Search trucks by unit number or VIN..."
            value={truckSearch}
            onChange={e => setTruckSearch(e.target.value)}
            className="w-full mb-4 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
            data-testid="truck-search-input"
          />
          <div className="max-h-96 overflow-y-auto space-y-2">
            {/* Skip option */}
            <div
              onClick={() => { setSelectedTruck(null); setWizardStep(3); }}
              className="p-4 rounded-xl bg-secondary/50 hover:bg-accent/20 cursor-pointer transition-colors flex items-center gap-3 border border-dashed border-border"
              data-testid="skip-truck-option"
            >
              <div className="w-10 h-10 rounded-full bg-gray-500/20 flex items-center justify-center">
                <Truck className="w-5 h-5 text-gray-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-muted-foreground">Skip - No specific truck</p>
                <p className="text-sm text-muted-foreground">Training applies to driver generally</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>

            {filteredTrucks.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No trucks found. Add trucks in the Fleet section first.
              </div>
            ) : (
              filteredTrucks.map(t => (
                <div
                  key={t.truck_id}
                  onClick={() => { setSelectedTruck(t); setWizardStep(3); }}
                  className="p-4 rounded-xl bg-secondary hover:bg-accent/20 cursor-pointer transition-colors flex items-center gap-3"
                  data-testid={`truck-option-${t.truck_id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{t.truck_number}</p>
                    <p className="text-sm text-muted-foreground">
                      {[t.make, t.model, t.year].filter(Boolean).join(' • ') || 'No details'}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Step 3: Select Video */}
      {wizardStep === 3 && (
        <div className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => setWizardStep(2)} className="p-2 hover:bg-secondary rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Video className="w-5 h-5 text-accent" />
              Select Training Video
            </h3>
          </div>

          {videos.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No training videos available. Add videos in the Video Library tab.
            </div>
          ) : (
            <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
              {videos.map(v => (
                <div
                  key={v.video_id}
                  onClick={() => { setSelectedVideo(v); setWizardStep(4); }}
                  className={`p-4 rounded-xl cursor-pointer transition-colors flex items-center gap-3 ${
                    selectedVideo?.video_id === v.video_id 
                      ? 'bg-accent/20 border-2 border-accent' 
                      : 'bg-secondary hover:bg-accent/10 border-2 border-transparent'
                  }`}
                  data-testid={`video-option-${v.video_id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    selectedVideo?.video_id === v.video_id ? 'bg-accent text-black' : 'bg-red-500/20'
                  }`}>
                    <Video className={`w-5 h-5 ${selectedVideo?.video_id === v.video_id ? '' : 'text-red-400'}`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{v.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {v.duration || 'Duration N/A'} • {v.description || 'No description'}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 4: Select Company & Send */}
      {wizardStep === 4 && (
        <div className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => showRandomDriver ? setWizardStep(1) : setWizardStep(3)} className="p-2 hover:bg-secondary rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-400" />
              Select Company Requesting Training
            </h3>
          </div>

          {/* For Random Driver - also select video here */}
          {showRandomDriver && !selectedVideo && (
            <div className="mb-6">
              <h4 className="text-md font-medium mb-3 text-muted-foreground">First, select a video:</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {videos.map(v => (
                  <div
                    key={v.video_id}
                    onClick={() => setSelectedVideo(v)}
                    className={`p-3 rounded-xl cursor-pointer transition-colors flex items-center gap-3 ${
                      selectedVideo?.video_id === v.video_id 
                        ? 'bg-accent/20 border-2 border-accent' 
                        : 'bg-secondary hover:bg-accent/10 border-2 border-transparent'
                    }`}
                  >
                    <Video className={`w-5 h-5 ${selectedVideo?.video_id === v.video_id ? 'text-accent' : 'text-red-400'}`} />
                    <span className="font-medium">{v.title}</span>
                    {selectedVideo?.video_id === v.video_id && <Check className="w-5 h-5 text-accent ml-auto" />}
                  </div>
                ))}
              </div>
            </div>
          )}

          <input
            type="text"
            placeholder="Search companies..."
            value={companySearch}
            onChange={e => setCompanySearch(e.target.value)}
            className="w-full mb-4 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-purple-400 outline-none"
            data-testid="company-search-input"
          />
          
          <div className="max-h-48 overflow-y-auto space-y-2 mb-6">
            {/* Skip option */}
            <div
              onClick={() => setSelectedCompany(null)}
              className={`p-4 rounded-xl cursor-pointer transition-colors flex items-center gap-3 border-2 ${
                selectedCompany === null ? 'border-purple-500 bg-purple-500/10' : 'border-dashed border-border hover:border-purple-400'
              }`}
              data-testid="skip-company-option"
            >
              <div className="w-10 h-10 rounded-full bg-gray-500/20 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-gray-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-muted-foreground">No specific company</p>
                <p className="text-sm text-muted-foreground">Skip company selection</p>
              </div>
              {selectedCompany === null && <Check className="w-5 h-5 text-purple-400" />}
            </div>

            {companies
              .filter(c => c.name?.toLowerCase().includes(companySearch.toLowerCase()))
              .map(c => (
                <div
                  key={c.company_id}
                  onClick={() => setSelectedCompany(c)}
                  className={`p-4 rounded-xl cursor-pointer transition-colors flex items-center gap-3 border-2 ${
                    selectedCompany?.company_id === c.company_id 
                      ? 'border-purple-500 bg-purple-500/10' 
                      : 'border-transparent bg-secondary hover:bg-purple-500/10'
                  }`}
                  data-testid={`company-option-${c.company_id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center overflow-hidden">
                    {c.logo_data ? (
                      <img src={`${API}/training/companies/${c.company_id}/logo`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Building2 className="w-5 h-5 text-purple-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{c.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {[c.dot_number && `DOT: ${c.dot_number}`, c.mc_number && `MC: ${c.mc_number}`].filter(Boolean).join(' • ') || 'No DOT/MC'}
                    </p>
                  </div>
                  {selectedCompany?.company_id === c.company_id && <Check className="w-5 h-5 text-purple-400" />}
                </div>
              ))}
          </div>

          {/* Email toggle & Send button */}
          {(selectedVideo || !showRandomDriver) && (
            <div className="space-y-4 border-t border-border/30 pt-4">
              <div className="flex items-center justify-between p-4 rounded-xl bg-secondary">
                <div>
                  <p className="font-medium">Send Email Notification</p>
                  <p className="text-sm text-muted-foreground">
                    Email the training link to {showRandomDriver ? randomDriverForm.email : (selectedDriver?.email || 'driver')}
                  </p>
                </div>
                <button 
                  onClick={() => setSendEmail(!sendEmail)}
                  className={`w-12 h-6 rounded-full transition-colors ${sendEmail ? 'bg-green-500' : 'bg-gray-600'}`}
                  data-testid="send-email-toggle"
                >
                  <div className={`w-5 h-5 rounded-full bg-white transform transition-transform ${sendEmail ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              <button
                onClick={sendTraining}
                disabled={sending || (!selectedVideo && showRandomDriver)}
                className="w-full py-4 bg-accent text-black rounded-xl font-bold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50"
                data-testid="send-training-btn"
              >
                {sending ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Training to {showRandomDriver ? randomDriverForm.full_name : selectedDriver?.name}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
